import { A, Icon, styled, theme } from 'design-system';

const StyledContainer = styled('div', {
  alignItems: 'center',
  textAlign: 'left',
  borderRadius: 24,
  width: '40%',
  padding: '30px',
  // Default styles applied if variant is not 'v1'
  backgroundColor: 'rgba(255, 255, 255)',
  border: `1px solid ${theme.colors.gray5}`,
  role: 'region',
  'aria-labelledby': 'card-heading',
  'aria-describedby': 'card-content',
  variants: {
    variant: {
      v1: {
        backgroundColor: 'rgba(5, 30, 87, 1)',
        border: 'none',
      },
      endnote: {
        backgroundColor: 'rgba(5, 30, 87, 1)',
        border: 'none',
      },
    },
  },
});

const StyledImageContainer = styled('div', {
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  // Default styles applied if variant is not 'v1'
  marginBottom: '10px',
  height: '120px',
  variants: {
    variant: {
      v1: {
        marginBottom: '20px',
        height: 'auto',
      },
      endnote: {
        marginBottom: '20px',
        height: 'auto',
      },
    },
  },
});

const ImgContainer = styled('img', {
  // Default styles applied if variant is not 'v1'
  height: 'auto',
  width: 'auto',
  variants: {
    variant: {
      v1: {
        height: '60px',
        width: '60px',
      },
      endnote: {
        height: '70px',
        width: '70px',
      },
    },
  },
  alt: 'Trust Card Image',
});

const StyledHeading = styled('div', {
  alignItems: 'center',
  fontWeight: 600,
  fontSize: 24,
  marginBottom: '6px',
  color: theme.colors.blue12.value,
  variants: {
    variant: {
      v1: {
        color: theme.colors.white.value,
        fontSize: 22,
        fontFamily: 'inherit',
      },
      endnote: {
        color: theme.colors.white.value,
        fontSize: 22,
        fontFamily: 'inherit',
        lineHeight: '34px',
        fontWeight: 700,
      },
    },
  },
});

const StyledHeadingLink = styled(A, {
  color: 'blue',
  textDecoration: 'underline',
  fontWeight: 600,
  fontSize: 24,
  display: 'inline-flex',
  alignItems: 'center',
  marginRight: '10px',
});

const StyledContent = styled('div', {
  lineHeight: '1.4',
  fontWeight: 400,
  fontSize: 16,
  marginTop: '15px',
  '& > div > a': {
    color: 'inherit',
  },
  // Default styles applied if variant is not 'v1'
  color: theme.colors.blue12.value,
  id: 'card-content',
  variants: {
    variant: {
      v1: {
        color: theme.colors.white.value,
      },
      endnote: {
        color: theme.colors.white.value,
        lineHeight: '24px',
      },
    },
  },
});

const StyledContentBottom = styled('div', {
  lineHeight: '24px',
  fontWeight: 600,
  fontSize: 14,
  alignItems: 'center',
  color: theme.colors.white.value,
  display: 'flex',
  marginTop: '12px',

  '& > div > a': {
    color: theme.colors.white.value,
    textDecoration: 'none',
    cursor: 'pointer',
    alignItems: 'center',
    display: 'flex',
    width: 'max-content',
  },

  variants: {
    variant: {
      v1: {},
      endnote: {
        '&:hover': {
          '& > div > a': {
            color: theme.colors.blue9.value,
          },
          color: theme.colors.blue9.value,
        },
      },
    },
  },
});

interface TrustCardProps {
  heading: string;
  content: string;
  leftImgSrc: string;
  hyperLinkText?: string;
  hyperLinkBottom?: string;
  variant?: 'v1' | 'endnote';
}

export const TrustCard = ({
  heading,
  content,
  leftImgSrc,
  hyperLinkText,
  hyperLinkBottom,
  variant,
}: TrustCardProps) => {
  return (
    <StyledContainer variant={variant}>
      <StyledImageContainer data-test-id="image_container" variant={variant}>
        <ImgContainer variant={variant} src={leftImgSrc} alt={heading} />
      </StyledImageContainer>
      <StyledHeading variant={variant}>
        {hyperLinkText ? (
          <>
            <StyledHeadingLink
              data-test-id="rejection_rate_link"
              href="/Paperpal Preflight - PRC 2022 (Reducing Rejection rates ).pdf"
              target="_blank"
              rel="noopener noreferrer"
            >
              {hyperLinkText}
            </StyledHeadingLink>{' '}
            {heading}
          </>
        ) : (
          heading
        )}
      </StyledHeading>
      <StyledContent data-test-id="commitement_content" variant={variant}>
        <div
          dangerouslySetInnerHTML={{
            __html: content ?? '',
          }}
        />
      </StyledContent>
      {['v1', 'endnote'].includes(variant as string) && hyperLinkBottom ? (
        <StyledContentBottom variant={variant}>
          <div
            data-test-id="commitement_content_link"
            style={{
              marginRight: '8px',
            }}
            dangerouslySetInnerHTML={{
              __html: hyperLinkBottom ?? '',
            }}
          />
          <Icon
            name="ArrowUpRightFromSquare"
            variant="regular"
            height={16}
            width={12}
          />
        </StyledContentBottom>
      ) : null}
    </StyledContainer>
  );
};

TrustCard.displayName = 'TrustCard';
